import React, { useCallback, useEffect, useMemo } from 'react';
import { Typography, Grid, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { isEqual, toNumber } from 'lodash';

import { GridActionsCellItem, GridActionsColDef, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Form } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import { divide } from 'shared/utils/numberOperations';
import { formatCurrencyFloat, formatInt, formatPercentageString } from 'shared/utils/numberFormatters';

import { taxesFormHelpers } from './taxesFormConfig';
import { useRootStore } from 'stores/RootStore';
import { Table } from 'components/shared/Table';

interface TaxLiabilityProps {
  form: Form;
}

interface TaxLiabilityRow {
  id: number | string;
  name?: string;
  item?: string;
  value?: number;
  editable?: boolean;
}

const TaxLiability: React.FC<TaxLiabilityProps> = observer(({ form }) => {
  const { underwritingPageStore } = useRootStore();
  const grossBuildingArea = underwritingPageStore.underwritingData?.property?.grossBuildingArea;
  const taxableAssessedValueField = form.$('incomeApproach.taxes.taxableAssessedValue');
  const taxRateField = form.$('incomeApproach.taxes.taxRate');
  const additionalTaxRatesField = form.$('incomeApproach.taxes.additionalTaxRates');
  const specialAssessmentsField = form.$('incomeApproach.taxes.specialAssessments');
  const taxLiabilityField = form.$('incomeApproach.taxes.taxLiability');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const additionalTaxRates: TaxLiabilityRow[] = additionalTaxRatesField.value || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const specialAssessments: TaxLiabilityRow[] = specialAssessmentsField.value || [];

  const taxRate = taxRateField.value || 0;
  const taxableAssessedValue = taxableAssessedValueField.value;
  const taxLiability = taxLiabilityField.value;

  useEffect(() => {
    taxesFormHelpers.calculateTaxLiability(form);
  }, [taxRate, taxableAssessedValue, additionalTaxRates, specialAssessments, form]);

  const taxLiabilityColDefs = useMemo(
    (): (GridColDef | GridActionsColDef)[] => [
      {
        field: 'item',
        headerName: 'Item',
        editable: true,
        width: 400,
        sortable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        valueFormatter: (value, row) => {
          const { name } = row;
          switch (name) {
            case 'taxRate':
            case 'additionalTaxRate':
              return formatPercentageString(toNumber(value));
            case 'taxLiability':
            case 'taxableAssessedValue':
            case 'taxLiabilityPSF':
            case 'specialAssessment':
              return formatCurrencyFloat(value);
            case 'grossBuildingArea':
              return formatInt(value);
            default:
              return value;
          }
        },
        editable: true,
        width: 200,
        flex: 1,
        sortable: false,
      },
      {
        field: 'action',
        headerName: 'Action',
        type: 'actions',
        width: 150,
        sortable: false,
        getActions: (params: GridRowParams) => {
          const { row } = params;
          if (row.name !== 'additionalTaxRate' && row.name !== 'specialAssessment') return [];

          return [
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const id = params.id.toString();
                if (id.startsWith('additional-tax-rate-')) {
                  const index = parseInt(id.replace('additional-tax-rate-', ''));
                  taxesFormHelpers.deleteAdditionalTaxRate(form, index);
                } else if (id.startsWith('special-assessment-')) {
                  const index = parseInt(id.replace('special-assessment-', ''));
                  taxesFormHelpers.deleteSpecialAssessment(form, index);
                }
              }}
            />,
          ];
        },
      },
    ],
    [form]
  );

  const addAdditionalTaxRate = useCallback(() => {
    taxesFormHelpers.addAdditionalTaxRate(form);
  }, [form]);

  const addSpecialAssessment = useCallback(() => {
    taxesFormHelpers.addSpecialAssessment(form);
  }, [form]);

  const processRowUpdate = (newRow: any, oldRow: any) => {
    if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
      return oldRow;
    }
    const { name } = newRow;
    const idStr = newRow.id.toString();
    switch (name) {
      case 'taxRate':
        taxRateField.set(parseFloat(newRow.value));
        return newRow;
      case 'taxableAssessedValue':
        taxableAssessedValueField.set(parseFloat(newRow.value));
        return newRow;
      case 'additionalTaxRate':
        if (idStr.startsWith('additional-tax-rate-')) {
          const index = parseInt(idStr.replace('additional-tax-rate-', ''));
          const currentAdditionalTaxRates = additionalTaxRatesField.value;
          const itemToUpdate = currentAdditionalTaxRates[index];
          if (itemToUpdate) {
            const id = itemToUpdate.id;
            itemToUpdate.taxRateName = newRow.item;
            itemToUpdate.taxRateValue = parseFloat(newRow.value);
            const updatedItems = currentAdditionalTaxRates.map((item: any) => (item.id === id ? itemToUpdate : item));
            additionalTaxRatesField.set(updatedItems);
          }
        }
        return newRow;
      case 'specialAssessment':
        if (idStr.startsWith('special-assessment-')) {
          const index = parseInt(idStr.replace('special-assessment-', ''));
          const currentSpecialAssessments = specialAssessmentsField.value;
          const itemToUpdate = currentSpecialAssessments[index];
          if (itemToUpdate) {
            const id = itemToUpdate.id;
            itemToUpdate.assessmentName = newRow.item;
            itemToUpdate.assessmentValue = parseFloat(newRow.value);
            const updatedItems = currentSpecialAssessments.map((item: any) => (item.id === id ? itemToUpdate : item));
            specialAssessmentsField.set(updatedItems);
          }
        }
        return newRow;
    }
    return oldRow;
  };

  const baseTaxLiabilityRows = useMemo(
    (): TaxLiabilityRow[] => [
      {
        id: 0,
        item: 'Taxable Assessed Value',
        name: 'taxableAssessedValue',
        value: taxableAssessedValue,
        editable: true,
      },
      {
        id: 1,
        item: 'Tax Rate',
        name: 'taxRate',
        value: taxRate,
        editable: true,
      },
      {
        id: 'total-liability',
        item: 'Tax Liability (Total)',
        name: 'taxLiability',
        value: taxLiability,
        editable: false,
      },
      {
        id: 3,
        item: 'Gross Building Area',
        name: 'grossBuildingArea',
        value: grossBuildingArea,
        editable: false,
      },
      {
        id: 'total-liability-psf',
        item: 'Tax Liability (PSF)',
        name: 'taxLiabilityPSF',
        value: divide(taxLiability, grossBuildingArea),
        editable: false,
      },
    ],
    [taxableAssessedValue, taxRate, taxLiability, grossBuildingArea]
  );

  const taxLiabilityRows = useMemo(() => {
    const combinedRows = [...baseTaxLiabilityRows];
    const currentAdditionalTaxRates = additionalTaxRatesField.value;
    currentAdditionalTaxRates.forEach((rate: any, index: number) => {
      const taxRateIndex = combinedRows.findIndex((r) => r.name === 'taxRate');
      combinedRows.splice(taxRateIndex + 1 + index, 0, {
        id: `additional-tax-rate-${index}`,
        item: rate.taxRateName,
        name: 'additionalTaxRate',
        value: rate.taxRateValue,
        editable: true,
      });
    });
    const currentSpecialAssessments = specialAssessmentsField.value;
    currentSpecialAssessments.forEach((assessment: any, index: number) => {
      const totalIndex = combinedRows.findIndex((r) => r.name === 'taxLiability');
      combinedRows.splice(totalIndex, 0, {
        id: `special-assessment-${index}`,
        item: assessment.assessmentName,
        name: 'specialAssessment',
        value: assessment.assessmentValue,
        editable: true,
      });
    });

    return combinedRows;
  }, [baseTaxLiabilityRows, additionalTaxRatesField.value, specialAssessmentsField.value]);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={9} container direction="row" justifyContent="space-between">
        <Typography variant="subtitle1">Tax Liability</Typography>
        <div>
          <Button sx={{ pr: 3 }} onClick={addAdditionalTaxRate}>
            + Additional Tax Rate
          </Button>
          <Button onClick={addSpecialAssessment}>+ Special Assessment</Button>
        </div>
      </Grid>
      <Grid
        item
        xs={9}
        sx={{
          minHeight: '150px',
          width: '100%',
          '& .total-row': {
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid',
            fontWeight: 'bold',
            '& .MuiDataGrid-cell': {
              fontWeight: 'bold',
            },
          },
        }}
      >
        <Table
          rows={taxLiabilityRows}
          columns={taxLiabilityColDefs}
          processRowUpdate={processRowUpdate}
          getRowClassName={(params) =>
            params.id === 'total-liability' || params.id === 'total-liability-psf' ? 'total-row' : ''
          }
          isCellEditable={(params) => {
            const { row } = params;
            return row?.editable ?? false;
          }}
          getCellClassName={(params) => {
            const { row } = params;
            if (!row.editable) {
              return 'disabled-cell';
            }
            return '';
          }}
        />
      </Grid>
    </Grid>
  );
});

export { TaxLiability };
